import React from 'react';
import { X, PenLine, Trash2, Search, Sparkles, BookOpen, ArrowRight, FileText, ChevronRight } from 'lucide-react';
import { DocumentAnnotation } from '../../types';

interface DocumentAnnotationsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  annotations: DocumentAnnotation[];
  onNavigateToAnnotation: (annotation: DocumentAnnotation) => void;
  onEditAnnotation: (annotation: DocumentAnnotation) => void;
  onDeleteAnnotation: (annotation: DocumentAnnotation) => Promise<void>;
  activeAnnotationId?: string | null;
}

export const DocumentAnnotationsDrawer: React.FC<DocumentAnnotationsDrawerProps> = ({
  isOpen,
  onClose,
  annotations,
  onNavigateToAnnotation,
  onEditAnnotation,
  onDeleteAnnotation,
  activeAnnotationId,
}) => {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [deletingId, setDeletingId] = React.useState<string | null>(null);

  // Filtrage par texte de recherche
  const filteredAnnotations = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return annotations;
    return annotations.filter((a) => {
      const matchNote = a.noteText?.toLowerCase().includes(q);
      const matchText = a.selectedText?.toLowerCase().includes(q);
      const matchPage = `page ${a.pageNumber}`.includes(q) || String(a.pageNumber) === q;
      const matchStruct = a.structuralReference?.toLowerCase().includes(q);
      return matchNote || matchText || matchPage || matchStruct;
    });
  }, [annotations, searchQuery]);

  if (!isOpen) return null;

  const handleDelete = async (annotation: DocumentAnnotation, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('Voulez-vous vraiment supprimer cette annotation ?')) {
      try {
        setDeletingId(annotation.id);
        await onDeleteAnnotation(annotation);
      } finally {
        setDeletingId(null);
      }
    }
  };

  return (
    <aside
      className="w-64 sm:w-72 shrink-0 border-l border-zinc-200 dark:border-zinc-800 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-md flex flex-col z-[10000] transition-all duration-200 shadow-xl h-full select-none"
      role="complementary"
      aria-label="Volet des annotations"
    >
      {/* Header */}
      <div className="p-2 border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50/70 dark:bg-zinc-900/70 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <div className="w-5 h-5 rounded-md bg-amber-500/10 text-amber-500 flex items-center justify-center">
            <PenLine className="w-3 h-3" />
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <h3 className="text-[10.5px] font-bold text-zinc-900 dark:text-zinc-100 uppercase tracking-wider">
                Mes annotations
              </h3>
              <span className="px-1.5 py-0.2 bg-amber-500 text-zinc-950 font-black rounded-full text-[8.5px]">
                {annotations.length}
              </span>
            </div>
            <p className="text-[8.5px] text-zinc-500 dark:text-zinc-400">
              Notes privées & extraits
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded-md text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 hover:bg-zinc-200/60 dark:hover:bg-zinc-800 transition cursor-pointer"
          title="Fermer le volet"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Barre de recherche interne */}
      {annotations.length > 0 && (
        <div className="p-1.5 border-b border-zinc-200/80 dark:border-zinc-800">
          <div className="relative">
            <Search className="w-2.5 h-2.5 text-zinc-400 absolute left-2 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Filtrer mes notes..."
              className="w-full pl-6 pr-2.5 py-0.8 text-[10.5px] bg-zinc-50 dark:bg-zinc-800/70 border border-zinc-200 dark:border-zinc-700 rounded-md text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-1 focus:ring-amber-500"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200"
              >
                <X className="w-2.5 h-2.5" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Contenu : Liste scrollable */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1.5 custom-scrollbar">
        {filteredAnnotations.length > 0 ? (
          filteredAnnotations.map((annotation) => {
            const isActive = activeAnnotationId === annotation.id;
            const color = annotation.colorHex || '#F59E0B';

            return (
              <div
                key={annotation.id}
                onClick={() => onNavigateToAnnotation(annotation)}
                style={{
                  borderColor: isActive ? color : undefined,
                  backgroundColor: isActive ? `${color}14` : undefined,
                  boxShadow: isActive ? `0 0 0 2px ${color}50, 0 4px 14px ${color}25` : undefined,
                }}
                className={`p-2 rounded-lg border transition-all cursor-pointer group text-left relative overflow-hidden ${
                  isActive
                    ? 'shadow-md scale-[1.01]'
                    : 'border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-850 hover:shadow-xs'
                }`}
              >
                {/* Bande latérale colorée (élargie si actif) */}
                <div
                  className={`absolute left-0 top-0 bottom-0 transition-all ${
                    isActive ? 'w-1.5 shadow-sm' : 'w-1'
                  }`}
                  style={{ backgroundColor: color }}
                />

                {/* Métadonnées : Page + Réf juridique + Badge Actif + Actions */}
                <div className="flex items-center justify-between mb-1 pl-1.5">
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className="text-[8.5px] font-mono font-bold px-1.2 py-0.2 rounded bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300">
                      P. {annotation.pageNumber}
                    </span>
                    {isActive && (
                      <span
                        className="text-[7.5px] font-extrabold px-1.5 py-0.2 rounded-full uppercase tracking-wider text-white shadow-xs flex items-center gap-0.5"
                        style={{ backgroundColor: color }}
                      >
                        ✓ Actif
                      </span>
                    )}
                    {annotation.structuralReference && (
                      <span
                        className="text-[8.5px] font-mono font-bold px-1.2 py-0.2 rounded flex items-center gap-0.5"
                        style={{
                          backgroundColor: `${color}18`,
                          color: color,
                        }}
                      >
                        <BookOpen className="w-2 h-2" />
                        {annotation.structuralReference}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-0.5 opacity-80 group-hover:opacity-100 transition-opacity">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditAnnotation(annotation);
                      }}
                      className="p-0.8 rounded text-zinc-400 hover:text-amber-600 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition cursor-pointer"
                      title="Modifier la note"
                    >
                      <PenLine className="w-2.5 h-2.5" />
                    </button>
                    <button
                      type="button"
                      disabled={deletingId === annotation.id}
                      onClick={(e) => handleDelete(annotation, e)}
                      className="p-0.8 rounded text-zinc-400 hover:text-rose-600 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition cursor-pointer disabled:opacity-40"
                      title="Supprimer l'annotation"
                    >
                      <Trash2 className="w-2.5 h-2.5" />
                    </button>
                  </div>
                </div>

                {/* Extrait surligné */}
                {annotation.selectedText && (
                  <div
                    className="pl-1.5 mb-1 border-l-2 text-[9px] font-serif italic text-zinc-600 dark:text-zinc-400 line-clamp-2 select-text"
                    style={{ borderLeftColor: color }}
                  >
                    « {annotation.selectedText} »
                  </div>
                )}

                {/* Note utilisateur */}
                {annotation.noteText ? (
                  <p className="text-[10px] font-sans text-zinc-800 dark:text-zinc-200 leading-snug font-medium pl-1.5 select-text whitespace-pre-wrap">
                    {annotation.noteText}
                  </p>
                ) : (
                  <p className="text-[9px] italic text-zinc-400 pl-1.5">
                    (Aucune note rédigée)
                  </p>
                )}

                {/* Date et lien d'accès direct arborant la couleur de la palette */}
                <div className="mt-1.5 pt-1 border-t border-zinc-100 dark:border-zinc-800/60 flex items-center justify-between text-[8px] text-zinc-400 pl-1.5">
                  <span>
                    {annotation.createdOn ? new Date(annotation.createdOn).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    }) : ''}
                  </span>
                  <span
                    className="flex items-center gap-1 font-bold group-hover:translate-x-0.5 transition-all text-[8.5px] px-1.5 py-0.5 rounded-md"
                    style={{
                      color: color,
                      backgroundColor: `${color}18`,
                      border: `1px solid ${color}40`,
                    }}
                  >
                    <span>Accéder</span>
                    <ChevronRight className="w-2.5 h-2.5 stroke-[2.5]" />
                  </span>
                </div>
              </div>
            );
          })
        ) : (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center text-zinc-400 space-y-3">
            <div className="w-12 h-12 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center text-zinc-400">
              <Sparkles className="w-6 h-6 text-amber-500/70" />
            </div>
            <div className="space-y-1">
              <p className="text-xs font-bold text-zinc-700 dark:text-zinc-300">
                {searchQuery ? 'Aucune annotation correspondante' : 'Aucune annotation sur cet acte'}
              </p>
              <p className="text-[11px] text-zinc-500 leading-relaxed">
                {searchQuery
                  ? 'Essayez avec un autre mot-clé ou effacez la recherche.'
                  : 'Sélectionnez du texte directement dans le visualiseur et cliquez sur « Annoter » pour créer votre première note contextuelle.'}
              </p>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
};
